/**
 * 画像調整パイプライン
 *
 * 既存のKonva.js版adjustContrast/adjustWhiteBalanceを活用して、
 * 複数の調整を順次適用するパイプライン処理を提供
 */

import { adjustContrast } from './contrastAdjustment';
import { adjustWhiteBalance } from './whiteBalance';
import { applyWatermark, WatermarkOptions } from './watermark';

export interface AdjustmentOptions {
  contrast?: number;        // -100 to 100
  brightness?: number;      // -100 to 100
  saturation?: number;      // -100 to 100
  whiteBalanceTemperature?: number;  // ケルビン値 (2750K, 4750K, 6750K など)
  whiteBalanceTint?: number;         // -100 to 100
  watermark?: WatermarkOptions;      // ウォーターマーク設定
}

/**
 * 画像に全ての調整を適用（パイプライン処理）
 *
 * 既存のKonva.js版adjustContrast/adjustWhiteBalanceを活用することで、
 * 高品質な画像処理を実現
 *
 * @param blob - 調整する画像Blob
 * @param options - 調整オプション
 * @returns 調整後の画像Blob
 *
 * @example
 * const adjustedBlob = await applyImageAdjustments(blob, {
 *   contrast: 20,
 *   brightness: 10,
 *   whiteBalanceTemperature: 4750
 * });
 */
export async function applyImageAdjustments(
  blob: Blob,
  options: AdjustmentOptions
): Promise<Blob> {
  console.log('[Pipeline] Starting with blob:', blob.size, 'bytes');
  console.log('[Pipeline] Options:', options);

  let processedBlob = blob;

  // 調整が何も指定されていない場合は早期リターン
  const hasContrastAdjustment = options.contrast !== 0 || options.brightness !== 0 || options.saturation !== 0;
  const hasWhiteBalanceAdjustment = options.whiteBalanceTemperature && options.whiteBalanceTemperature !== 0;
  const hasWatermark = options.watermark !== undefined;

  console.log('[Pipeline] hasContrastAdjustment:', hasContrastAdjustment);
  console.log('[Pipeline] hasWhiteBalanceAdjustment:', hasWhiteBalanceAdjustment);
  console.log('[Pipeline] hasWatermark:', hasWatermark);

  if (!hasContrastAdjustment && !hasWhiteBalanceAdjustment && !hasWatermark) {
    console.log('[Pipeline] No adjustments, returning original blob');
    return blob;
  }

  try {
    // Step 1: コントラスト・明るさ・彩度調整（Konva.js版）
    if (hasContrastAdjustment) {
      console.log('[Pipeline] Applying contrast adjustment...');
      const result = await adjustContrast(processedBlob, {
        brightness: options.brightness || 0,
        contrast: options.contrast || 0,
        saturation: options.saturation || 0,
      });
      processedBlob = result.blob;
      console.log('[Pipeline] Contrast adjustment complete, blob size:', processedBlob.size);
    }

    // Step 2: ホワイトバランス調整（Konva.js版）
    if (hasWhiteBalanceAdjustment) {
      console.log('[Pipeline] Applying white balance adjustment...');
      const result = await adjustWhiteBalance(processedBlob, {
        temperature: options.whiteBalanceTemperature!,
        tint: options.whiteBalanceTint || 0,
      });
      processedBlob = result.blob;
      console.log('[Pipeline] White balance adjustment complete, blob size:', processedBlob.size);
    }

    // Step 3: ウォーターマーク適用
    if (hasWatermark && options.watermark) {
      console.log('[Pipeline] Applying watermark...');
      const result = await applyWatermark(processedBlob, options.watermark);
      processedBlob = result.blob;
      console.log('[Pipeline] Watermark complete, blob size:', processedBlob.size);
    }

    console.log('[Pipeline] All adjustments complete, final blob size:', processedBlob.size);
    return processedBlob;
  } catch (error) {
    console.error('[Pipeline] Image adjustment pipeline failed:', error);
    throw new Error('画像調整の適用に失敗しました');
  }
}

/**
 * Redux featuresから調整オプションを抽出
 *
 * @param features - ImageEditingStateのfeaturesオブジェクト
 * @returns AdjustmentOptions
 */
export function extractAdjustmentOptions(features: any): AdjustmentOptions {
  // 各機能のenabled状態をチェックして、OFFの場合は0を返す
  // 注意: brightnessは別featureとして管理されている
  const options: AdjustmentOptions = {
    contrast: features.contrastAdjustment?.enabled ? (features.contrastAdjustment.settings?.contrast || 0) : 0,
    brightness: features.brightnessAdjustment?.enabled ? (features.brightnessAdjustment.settings?.brightness || 0) : 0,
    saturation: features.contrastAdjustment?.enabled ? (features.contrastAdjustment.settings?.saturation || 0) : 0,
    whiteBalanceTemperature: features.whiteBalance?.enabled ? (features.whiteBalance.settings?.temperature || 0) : 0,
    whiteBalanceTint: features.whiteBalance?.enabled ? (features.whiteBalance.settings?.tint || 0) : 0,
  };

  // ウォーターマークが有効な場合は追加
  if (features.watermark?.enabled && features.watermark.settings) {
    options.watermark = features.watermark.settings as WatermarkOptions;
  }

  return options;
}
